import React from "react";
import { Box, List, ListItemButton, IconButton } from "@mui/material";
import { IconsBarProps, SquareIconButtonProps } from "../types";
import { DividerIcons, UserProfileImage } from "../styles";
import { getBrandCustom } from "Util/getCustomColor";
import { ROW_H } from "../constants";

const SquareIconButton = React.memo(
  ({
    k,
    label,
    onHover,
    onClick,
    active,
    children,
    pinned,
    defaultColor,
    hoverColor,
    hoverBg,
  }: SquareIconButtonProps) => {
    return (
      <ListItemButton
        onMouseEnter={() => !pinned && onHover?.()}
        onClick={onClick}
        sx={{
          display: "flex",
          width: 50,
          height: ROW_H,
          justifyContent: "center",
          margin: "auto",
          borderRadius: 1,
          fontWeight: pinned ? 700 : 400,
          color: active ? hoverColor : defaultColor,
          bgcolor: active ? hoverBg : "transparent",
          "&:hover": { backgroundColor: hoverBg, color: hoverColor },
          transition: "background-color .15s ease, color .15s ease",
        }}
        aria-label={label ?? k}
      >
        {children}
      </ListItemButton>
    );
  }
);

const IconsBar: React.FC<IconsBarProps> = ({
  items,
  logoSrc,
  activeKey,
  onHoverIcon,
  onLogoClick,
  sidebarIcon,
  show = true,
  width,
  iconSize = 17,
  defaultColor = getBrandCustom("darkPrimaryColor", "#E4E7EC"),
  hoverColor = "#F9FAFB",
  hoverBg = getBrandCustom("pressedColor", "#3377B6"),
  pinned = false,
  bottomActions = [],
  avatarUrl,
  onAvatarClick,
}) => {
  if (!show) return null;

  const renderMaskedIcon = (src: string) => (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: iconSize,
        height: iconSize,
        display: "block",
        bgcolor: "currentColor",
        mask: `url(${src}) no-repeat center`,
        maskSize: "contain",
        WebkitMask: `url(${src}) no-repeat center`,
        WebkitMaskSize: "contain",
      }}
    />
  );

  const mainItems = items.filter((item) => item.key !== "settings");
  const settingsItem = items.find((item) => item.key === "settings");

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        width,
        overflowX: "hidden",
        overflowY: "auto",
        boxShadow: "none",
        borderRight: `1px solid ${getBrandCustom("headerBackgroundColor", "#0F3B63")}`,
        bgcolor: getBrandCustom("headerBackgroundColor", "#002445"),
        zIndex: 1103,
        display: "flex",
        flexDirection: "column",
        scrollBehavior: "smooth",
        "&::-webkit-scrollbar": {
          width: "0.25rem",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#3377B6",
          borderRadius: "0.25rem",
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: 56 }}>
        {onLogoClick ? (
          <IconButton onClick={onLogoClick} sx={{ p: 0 }}>
            {sidebarIcon ?? <img src={logoSrc} alt="Shipeezi Logo" height={25} />}
          </IconButton>
        ) : (
          sidebarIcon ?? <img src={logoSrc} alt="Shipeezi Logo" height={25} />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <List sx={{ py: 0 }}>
          {mainItems.map((item) => (
            <SquareIconButton
              key={item.key}
              k={item.key}
              label={item.label}
              active={activeKey === item.key}
              pinned={pinned}
              defaultColor={defaultColor}
              hoverColor={hoverColor}
              hoverBg={hoverBg}
              onHover={() => onHoverIcon(item.key)}
              onClick={() => onHoverIcon(item.key)}
            >
              {item.icon && renderMaskedIcon(item.icon)}
            </SquareIconButton>
          ))}

          <DividerIcons sx={{ my: 1 }} />

          {settingsItem && (
            <SquareIconButton
              key={settingsItem.key}
              k={settingsItem.key}
              label={settingsItem.label}
              active={activeKey === settingsItem.key}
              pinned={pinned}
              defaultColor={defaultColor}
              hoverColor={hoverColor}
              hoverBg={hoverBg}
              onHover={() => onHoverIcon(settingsItem.key)}
              onClick={() => onHoverIcon(settingsItem.key)}
            >
              {settingsItem.icon && renderMaskedIcon(settingsItem.icon)}
            </SquareIconButton>
          )}
        </List>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "stretch",
          paddingBottom: "10px",
        }}
      >
        <Box sx={{ py: 0, display: "flex", flexDirection: "column" }}>
          {bottomActions.map((action) => (
            <SquareIconButton
              key={`bottom-${action.key}`}
              k={action.key}
              label={action.ariaLabel ?? action.key}
              pinned={pinned}
              defaultColor={defaultColor}
              hoverColor={hoverColor}
              hoverBg={hoverBg}
              onClick={action.onClick}
              active={activeKey === action.key}
            >
              {renderMaskedIcon(action.icon)}
            </SquareIconButton>
          ))}
        </Box>

        <DividerIcons sx={{ my: 1 }} />

        <List
          onClick={onAvatarClick}
          sx={{
            py: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
          aria-label="profile"
        >
          <UserProfileImage src={avatarUrl} alt="user profile" />
        </List>
      </Box>
    </Box>
  );
};

export default IconsBar;
